import React from 'react';
import { Box, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';

const pulse = keyframes`
  0% {
    opacity: 0.35;
    transform: scale(0.95);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.35;
    transform: scale(0.95);
  }
`;

const waveShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0) translateX(0);
    opacity: 0.5;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    transform: translateY(-20px) translateX(10px);
    opacity: 0.5;
  }
`;

const gridMove = keyframes`
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 40px 40px;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const drift = keyframes`
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 0.4;
  }
  25% {
    opacity: 0.8;
  }
  50% {
    transform: translate(30px, -30px) scale(1.2);
    opacity: 0.6;
  }
  75% {
    opacity: 0.9;
  }
  100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.4;
  }
`;

const auroraMove = keyframes`
  0% {
    transform: translateY(0) scale(1);
    opacity: 0.6;
  }
  50% {
    transform: translateY(-40px) scale(1.1);
    opacity: 0.9;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 0.6;
  }
`;

const binaryFall = keyframes`
  0% {
    transform: translateY(-20px);
    opacity: 0;
  }
  10% {
    opacity: 0.6;
  }
  90% {
    opacity: 0.6;
  }
  100% {
    transform: translateY(100vh);
    opacity: 0;
  }
`;

const PARTICLE_COUNT = 60;

const PARTICLE_COLORS = ['#00d4ff', '#ff4081', '#00ff88', '#ffffff'];

const randomBetween = (min, max) => Math.random() * (max - min) + min;

const generateParticles = (colorPool = PARTICLE_COLORS) => {
  const particles = [];
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    particles.push({
      id: i,
      size: randomBetween(3, 8),
      left: `${randomBetween(0, 100)}%`,
      top: `${randomBetween(0, 100)}%`,
      delay: randomBetween(0, 6),
      duration: randomBetween(10, 24),
      color: colorPool[Math.floor(Math.random() * colorPool.length)],
      blinkDuration: randomBetween(2, 4),
    });
  }
  return particles;
};

const BINARY_CHARS = '01';

const AnimatedBackground = ({ forceType }) => {
  const theme = useTheme();
  const bg = theme.custom || {};
  const bgType = forceType ?? bg.backgroundType ?? 'animated';
  const bgAnimation = bg.backgroundAnimation ?? 'pulse';
  const bgColor = bg.backgroundColor ?? '#030710';
  const bgGradient = bg.backgroundGradient;
  const bgImage = bg.backgroundImage;
  const bgOpacity = typeof bg.backgroundOpacity === 'number' ? bg.backgroundOpacity : 1;

  const primaryMain = theme.palette.primary.main;
  const secondaryMain = theme.palette.secondary.main;
  const successMain = theme.palette.success.main;

  const gradientFallback = `radial-gradient(circle at 50% 50%, #0a0f1d 0%, #02040a 100%)`;
  const gradientValue = bgGradient || gradientFallback;

  const coloredParticles = React.useMemo(() => generateParticles([primaryMain, secondaryMain, successMain, '#ffffff']), [primaryMain, secondaryMain, successMain]);

  const renderAnimation = () => {
    switch (bgAnimation) {
      case 'particles':
        return (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(circle at 20% 20%, ${successMain}22, transparent 45%), radial-gradient(circle at 80% 10%, ${primaryMain}22, transparent 50%), ${bgColor}`,
              }}
            />
            {coloredParticles.map((p) => (
              <Box
                key={p.id}
                sx={{
                  position: 'absolute',
                  width: `${p.size}px`,
                  height: `${p.size}px`,
                  left: p.left,
                  top: p.top,
                  background: `radial-gradient(circle, ${p.color}aa, transparent 70%)`,
                  borderRadius: '50%',
                  boxShadow: `0 0 ${p.size * 2}px ${p.color}66`,
                  animation: `${drift} ${p.duration}s ease-in-out ${p.delay}s infinite alternate`,
                  opacity: 0.6,
                }}
              />
            ))}
          </Box>
        );

      case 'nebula':
        return (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `${bgColor}`,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(circle at 15% 30%, ${primaryMain}28, transparent 45%), radial-gradient(circle at 85% 70%, ${secondaryMain}28, transparent 50%), radial-gradient(circle at 50% 15%, ${successMain}1e, transparent 45%)`,
                animation: `${waveShift} 28s ease-in-out infinite`,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(circle at 70% 40%, ${secondaryMain}22, transparent 45%), radial-gradient(circle at 30% 80%, ${primaryMain}22, transparent 50%)`,
                animation: `${waveShift} 36s ease-in-out 2s infinite reverse`,
              }}
            />
            {coloredParticles.map((p) => (
              <Box
                key={p.id}
                sx={{
                  position: 'absolute',
                  width: `${p.size}px`,
                  height: `${p.size}px`,
                  left: p.left,
                  top: p.top,
                  background: `radial-gradient(circle, ${p.color}aa, transparent 70%)`,
                  borderRadius: '50%',
                  animation: `${drift} ${p.duration}s ease-in-out ${p.delay}s infinite alternate`,
                  opacity: 0.4,
                }}
              />
            ))}
          </Box>
        );

      case 'aurora':
        return (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `${bgColor}`,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `
                  linear-gradient(160deg, ${primaryMain}28 0%, ${secondaryMain}22 50%, transparent 100%),
                  linear-gradient(200deg, ${successMain}1e 0%, ${primaryMain}24 50%, transparent 100%)
                `,
                backgroundSize: '200% 200%',
                filter: 'blur(30px)',
                animation: `${auroraMove} 16s ease-in-out infinite`,
                opacity: 0.7,
              },
            }}
          />
        );

      case 'binary':
        return (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `${bgColor}`,
              overflow: 'hidden',
            }}
          >
            {Array.from({ length: 60 }).map((_, i) => {
              const col = (i * 7) % 100;
              const delay = (i * 0.4) % 8;
              const char = BINARY_CHARS[Math.floor(Math.random() * BINARY_CHARS.length)];
              return (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    left: `${col}%`,
                    top: '-20px',
                    color: `${primaryMain}66`,
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    animation: `${binaryFall} 6s linear ${delay}s infinite`,
                  }}
                >
                  {char}
                </Box>
              );
            })}
          </Box>
        );

      case 'constellation':
        return (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at 30% 30%, ${primaryMain}1a, transparent 45%), radial-gradient(circle at 70% 70%, ${secondaryMain}1a, transparent 50%), ${bgColor}`,
            }}
          >
            {coloredParticles.map((p) => (
              <Box
                key={`p-${p.id}`}
                sx={{
                  position: 'absolute',
                  width: `${p.size}px`,
                  height: `${p.size}px`,
                  left: p.left,
                  top: p.top,
                  background: p.color,
                  borderRadius: '50%',
                  animation: `${drift} ${p.duration}s ease-in-out ${p.delay}s infinite alternate`,
                  opacity: 0.8,
                  boxShadow: `0 0 12px ${p.color}`,
                }}
              />
            ))}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                  radial-gradient(circle at 20% 20%, ${primaryMain}44 1px, transparent 1px),
                  radial-gradient(circle at 80% 30%, ${secondaryMain}44 1px, transparent 1px),
                  radial-gradient(circle at 50% 80%, ${successMain}44 1px, transparent 1px)
                `,
                backgroundSize: '30px 30px',
                opacity: 0.5,
              }}
            />
          </Box>
        );

      case 'waves':
        return (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at 20% 20%, ${successMain}33, transparent 45%), radial-gradient(circle at 80% 10%, ${primaryMain}33, transparent 50%), ${bgColor}`,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(120deg, ${primaryMain}33, ${secondaryMain}26, rgba(0, 0, 0, 0)), linear-gradient(210deg, ${primaryMain}33, rgba(0, 0, 0, 0), ${secondaryMain}26)`,
                backgroundSize: '300% 300%',
                filter: 'blur(60px)',
                animation: `${waveShift} 20s ease-in-out infinite`,
              },
            }}
          />
        );

      case 'grid':
        return (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `${bgColor}`,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                  linear-gradient(rgba(0, 212, 255, 0.06) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(0, 212, 255, 0.06) 1px, transparent 1px)
                `,
                backgroundSize: '20px 20px',
                animation: `${gridMove} 30s linear infinite`,
              },
            }}
          />
        );

      case 'none':
        return (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: bgColor,
            }}
          />
        );

      case 'pulse':
      default:
        return (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at 20% 20%, ${successMain}33, transparent 45%), radial-gradient(circle at 80% 10%, ${primaryMain}33, transparent 50%), ${bgColor}`,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: '140%',
                height: '140%',
                top: '-40%',
                left: '-20%',
                background: `linear-gradient(120deg, ${primaryMain}33, ${secondaryMain}26, rgba(0, 0, 0, 0))`,
                filter: 'blur(80px)',
                animation: `${pulse} 12s ease-in-out infinite`,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: '80%',
                height: '80%',
                borderRadius: '50%',
                top: '10%',
                right: '-5%',
                background: `conic-gradient(from 90deg, rgba(255, 255, 255, 0.05), ${successMain}66, ${primaryMain}1a)`,
                animation: `${pulse} 18s ease-in-out infinite`,
                opacity: 0.6,
              }}
            />
          </Box>
        );
    }
  };

  if (bgType === 'static') {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: -1,
          backgroundColor: bgColor,
          opacity: bgOpacity,
        }}
      />
    );
  }

  if (bgType === 'gradient') {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: -1,
          background: gradientValue,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: bgOpacity,
        }}
      />
    );
  }

  if (bgType === 'image') {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: -1,
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: bgColor,
          opacity: bgOpacity,
        }}
      />
    );
  }

  if (bgType === 'none') {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: -1,
          backgroundColor: '#0a0f1d',
          opacity: bgOpacity,
        }}
      />
    );
  }

  // animated (default)
  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        overflow: 'hidden',
        opacity: bgOpacity,
      }}
    >
      {renderAnimation()}
    </Box>
  );
};

export default AnimatedBackground;
